import { SyncActionTypeData, SyncActionTypeId } from "src/app/constant/sync-action-constants";

export interface SyncAction<T extends SyncActionTypeId> {
  /** The ID of the sync action type. */
  typeId: T;
}

/**
 * Interface for a sync action before it is saved to the database.
 * @template T - The ID corresponding to the sync action type.
 */
export interface UnsavedSyncAction<T extends SyncActionTypeId> extends SyncAction<T> {
  /** 
   * Values inserted into URL of sync action type. 
   * If the URL has a placeholder (denoted by curly braces) in its path matching a property in param object, it will be subsituted. 
   * All other properties in param object will be added to the URL as query params. 
   */
  params: SyncActionTypeData[T]["params"];

  /** Auxillary data that is used in doBefore, onSuccess, and/or onFailure functions for the sync action. The type of the auxillary data depends on the sync action type. */
  auxillaryData: SyncActionTypeData[T]["auxillaryData"];

  /** The payload of the sync action. The type of the payload depends on the sync action type. */
  payload: SyncActionTypeData[T]["payload"];

  /** The current status of the sync action. */
  status: SyncActionStatus;

  /** The date when the sync action was created, in ISO 8601 format. */
  createdDate: string;
  
  /** An array of attempts that have been made to perform the sync action. */
  attempts: SyncActionAttempt[];
}

/**
 * Interface for a sync action after it is saved to the database.
 * @template T - The Id corresponding to the SyncActionType
 */
export interface SavedSyncAction<T extends SyncActionTypeId> extends UnsavedSyncAction<T> {
  /** The primary key of the sync action. */
  RecordId: number;
}

/**
 * Interface for a sync action after it is saved to the database.
 * @template T - The Id corresponding to the SyncActionType
 */
export interface CompletedSyncAction<T extends SyncActionTypeId> extends SavedSyncAction<T> {
  /** The date when the sync action was completed (either success or failed), in ISO 8601 format. */
  completedDate: string;

  /** The server's response to the sync action. The type of the response depends on the sync action type. */
  response: SyncActionTypeData[T]["response"];
}

export interface SyncActionAttempt {
  attemptedDate: string;
  outcome?: string;
}

export enum SyncActionStatus {
  Queued = 'Queued',
  Success = 'Success',
  Failed = 'Failed',
} 